/**
 * 应用程序主组件
 * 使用MVVM架构模式
 */

import React from 'react';
import { AppProvider } from './viewmodels/appviewmodel.jsx';
import { ThemeProvider } from './design-system/hooks/useTheme.jsx';
import { DesignSystemProvider } from './viewmodels/designsystemviewmodel.jsx';
import MainLayout from './views/layouts/MainLayout.jsx';
import DesignSystemPreview from './views/design/DesignSystemPreview.jsx';
import TodayInHistory from './views/components/TodayInHistory.jsx';
import WeatherWidget from './views/components/WeatherWidget.jsx';
import './App.css';

/**
 * 应用程序主组件
 */
function App() {
  return (
    <AppProvider>
      <ThemeProvider>
        <DesignSystemProvider>
          <MainLayout>
            <div className="space-y-8">
              {/* 头部信息区域 */}
              <section className="space-y-4">
                {/* 历史上的今天 */}
                <TodayInHistory className="mb-4" />
                
                {/* 天气信息 */}
                <div className="flex justify-end">
                  <WeatherWidget className="max-w-sm" />
                </div>
              </section>

              {/* 欢迎区域 */}
              <section className="text-center py-12">
                <h1 className="text-4xl font-bold text-foreground mb-4">
                  欢迎来到 Liu Er Site
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  基于 MVVM 架构的现代化 React 应用
                </p>
                <div className="flex justify-center space-x-4">
                  <div className="flex items-center space-x-2 px-4 py-2 bg-card border border-border rounded-lg">
                    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-sm font-medium">React 18</span>
                  </div>
                  <div className="flex items-center space-x-2 px-4 py-2 bg-card border border-border rounded-lg">
                    <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">MVVM 架构</span>
                  </div>
                  <div className="flex items-center space-x-2 px-4 py-2 bg-card border border-border rounded-lg">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">设计系统</span>
                  </div>
                </div>
              </section>

              {/* 设计系统预览 */}
              <section>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    设计系统预览
                  </h2>
                  <p className="text-muted-foreground">
                    展示应用中使用的设计组件和样式系统
                  </p>
                </div>
                <DesignSystemPreview />
              </section>

              {/* 架构说明 */}
              <section className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-2xl font-bold text-foreground mb-4">
                  MVVM 架构说明
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">
                      Model (模型层)
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      负责数据管理和业务逻辑，包括用户模型、主题模型、表单模型等。
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">
                      View (视图层)
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      负责UI展示，包括页面组件、布局组件、表单组件等。
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">
                      ViewModel (视图模型层)
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      连接Model和View，管理状态和业务逻辑，提供数据绑定。
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </MainLayout>
        </DesignSystemProvider>
      </ThemeProvider>
    </AppProvider>
  );
}

export default App;
